import {
  inject,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterRenderEffect,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CompanyService } from '../../../../../../services/company.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideImage, lucideInfo, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { ElementIssues } from '../../../../voucher-design-editor.store';
import { CatalogEntry, DesignElement } from '../../../../../../services/voucher-design.service';
import {
  PlaceholderMap,
  placeholderSample,
  renderOf,
  titleOf,
  tokenize,
  wouldPrint,
} from '../../../../shared/placeholders';
import {
  HANDLES,
  Handle,
  PX_PER_PT,
  elementClasses,
  elementStyle,
} from '../../../../editor-geometry';
import { fitVoucherTextBox } from '../../../../shared/fit-text';
import { BoxShape, POLYGONS, SVG_SHAPES } from '../../../../shared/shapes';
import { STATIC_QR, STATIC_QR_WITH_QUIET_ZONE } from '../../../../shared/static-qr';
import { PlaceholderFieldComponent } from '../../../placeholder-field/placeholder-field.component';

/** A drag (move) or resize (one of eight handles) starting on this element. */
export interface PointerStart {
  id: string;
  /** `pan` moves the crop of a picture that fills its box (⌥ + drag). */
  mode: 'move' | 'resize' | 'pan';
  handle?: Handle;
  event: PointerEvent;
  /** For a pan: the picture's pixel size, to know how far it can shift. */
  natural?: { width: number; height: number };
  /** The element was selected before this press: a click without a drag then
   * selects the part inside its group. */
  wasSelected?: boolean;
}

/** Keyboard resize: the handle's edge moves by (dx, dy) pt. */
export interface KeyResize {
  id: string;
  handle: Handle;
  dx: number;
  dy: number;
}

/** The messages start with the element's title; the badge sits on it already. */
function stripLabel(message: string, title: string): string {
  const prefix = `${title}: `;
  return message.startsWith(prefix) ? message.slice(prefix.length) : message;
}

/** A value's run on the canvas, told apart from own words; editor only, the print has no tint. */
const PLACEHOLDER_TINT =
  'background:rgba(168,85,247,.14);box-shadow:inset 0 -1px 0 rgba(168,85,247,.55);border-radius:1pt;';

/** A piece of the shown text: a placeholder's sample, or the merchant's own words. */
interface ShownRun {
  text: string;
  placeholder: boolean;
  /** The value's name, shown as a small label on the run. */
  label: string;
}

/** A value's name over its run on the canvas, at the run's top right corner (pt). */
interface ValueLabel {
  text: string;
  x: number;
  y: number;
}

const HANDLE_LABELS: Record<Handle, string> = {
  n: 'oben',
  ne: 'oben rechts',
  e: 'rechts',
  se: 'unten rechts',
  s: 'unten',
  sw: 'unten links',
  w: 'links',
  nw: 'oben links',
};

/**
 * One element on the canvas: the same box the PDF prints (shared CSS, pt units,
 * the fit-to-box script), plus a focusable surface to select, drag and nudge it
 * and, when selected, eight resize handles.
 */
@Component({
  selector: 'app-canvas-element',
  standalone: true,
  imports: [NgIcon, HlmTooltipImports, PlaceholderFieldComponent],
  providers: [provideIcons({ lucideImage, lucideInfo, lucideTriangleAlert })],
  templateUrl: './canvas-element.component.html',
  styleUrls: ['../../../../shared/voucher-elements.css', './canvas-element.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'absolute block',
    '[style.left.pt]': 'element().x',
    '[style.top.pt]': 'element().y',
    '[style.width.pt]': 'element().width',
    '[style.height.pt]': 'element().height',
    '[attr.data-id]': 'element().id',
    '[class.muted]': 'muted()',
    '[class.is-editing]': 'editing()',
  },
})
export class CanvasElementComponent {
  readonly element = input.required<DesignElement>();
  private readonly companyService = inject(CompanyService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  readonly entry = input<CatalogEntry | undefined>(undefined);
  /** The catalog's placeholders by key: samples and labels for the texts. */
  readonly placeholders = input<PlaceholderMap>(new Map());
  readonly fontCss = input.required<(family: string) => string>();
  /** The picture an image element shows, or null for a placeholder. */
  readonly imageSrc = input<string | null>(null);
  /** Variable texts at their planned length instead of the short samples. */
  readonly longTexts = input(false);
  readonly selected = input(false);
  /** Resize handles only when this is the one selected element. */
  readonly showHandles = input(false);
  /** A guide runs through this element: show what the selection snapped to. */
  readonly snapTarget = input(false);
  /** The element's errors and warnings: a badge in its corner tells them on hover. */
  readonly issues = input<ElementIssues | null>(null);
  /** The canvas zoom, so the badge keeps its screen size. */
  readonly scale = input(1);
  /** A dashed outline always, not only on hover and selection. */
  readonly outlined = input(false);
  /** The text is being edited in place: the chip field lies over the box. */
  readonly editing = input(false);
  /** The editing starts with the whole text selected (a fresh text's placeholder words). */
  readonly selectAll = input(false);

  /** A click: `toggle` with Shift or Cmd/Ctrl adds to or removes from the selection. */
  readonly select = output<{ id: string; toggle: boolean }>();
  readonly nudge = output<{ dx: number; dy: number }>();
  readonly remove = output<string>();
  readonly pointerStart = output<PointerStart>();
  readonly keyResize = output<KeyResize>();
  /** A double-click on a text: edit it where it is. */
  readonly editStart = output<string>();
  readonly editEnd = output<void>();
  readonly contentChange = output<{ id: string; content: string }>();

  readonly handles = HANDLES;
  readonly handleLabels = HANDLE_LABELS;
  private readonly box = viewChild<ElementRef<HTMLElement>>('box');
  private readonly img = viewChild<ElementRef<HTMLImageElement>>('img');
  /** A picture that fills its box can be panned; the hint under it says how. */
  readonly canPan = computed(
    () =>
      this.entry()?.render === 'image' &&
      (this.element().fit ?? 'cover') === 'cover' &&
      !!this.imageSrc(),
  );
  readonly panHint = `${/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Strg'} + Ziehen verschiebt den Bildausschnitt`;
  /** CSS object-position of the picture: the part that stays in view when cropped. */
  readonly objectPosition = computed(() => {
    const e = this.element();
    return `${(e.focus_x ?? 0.5) * 100}% ${(e.focus_y ?? 0.5) * 100}%`;
  });

  readonly render = computed(() => renderOf(this.element(), this.entry()));
  readonly isText = computed(() => this.entry()?.render === 'text');
  readonly qr = computed(() =>
    this.element().qr_quiet_zone ? STATIC_QR_WITH_QUIET_ZONE : STATIC_QR,
  );
  readonly qrColor = computed(() => this.element().qr_color ?? '#000000');
  /** A box drawn as SVG (triangle, diamond, star, line), or null for CSS shapes. */
  readonly drawnShape = computed(() => {
    const shape = this.element().shape as BoxShape | undefined;
    return this.render() === 'box' && shape && SVG_SHAPES.has(shape) ? shape : null;
  });
  readonly polygon = computed(() => POLYGONS[this.drawnShape() ?? ''] ?? '');
  readonly lineIsWide = computed(() => this.element().width >= this.element().height);
  readonly strokeColor = computed(() =>
    (this.element().border_width ?? 0) > 0 ? (this.element().border_color ?? '#000000') : 'none',
  );
  readonly strokeWidth = computed(() => `${this.element().border_width ?? 0}pt`);
  /** The logo's colour: its own, else the shop's primary colour (as the backend prints it). */
  readonly brandColor = computed(
    () =>
      this.element().brand_color ??
      this.companyService.company()?.voucher_app_primary_color ??
      '#5271ff',
  );
  /** The logo artwork from the catalogue, coloured, as an image (no HTML from the API). */
  readonly brandSrc = computed(() => {
    const svg = (this.entry()?.artwork ?? '').replace(/currentColor/g, this.brandColor());
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
  readonly qrBackground = computed(() => this.element().qr_background ?? '#ffffff');
  readonly label = computed(() => titleOf(this.element(), this.entry(), this.placeholders()));
  readonly classes = computed(() => elementClasses(this.element(), this.entry()));
  readonly style = computed(() => elementStyle(this.element(), this.entry(), this.fontCss()));
  /** The in-place editor in the box's own font; a one-line text runs on past the box. */
  readonly editorStyle = computed(
    () =>
      elementStyle(this.element(), this.entry(), this.fontCss()) +
      `white-space:${this.element().wrap ? 'pre-wrap' : 'nowrap'};`,
  );

  onDoubleClick(event: MouseEvent) {
    event.stopPropagation();
    if (this.isText()) this.editStart.emit(this.element().id);
  }
  /** What an image element without a picture says: the kind, and why it is empty. */
  readonly placeholderText = computed(() => {
    const source = this.entry()?.image_source;
    if (source === 'logo') return 'Logo fehlt';
    if (source === 'banner') return 'Banner fehlt';
    if (source === 'product') return 'Produktbild';
    return 'Bild';
  });
  /** The text as the voucher would show it: own words as they are, placeholders as
   * their samples (the company's values, the demo voucher's), each run marked so
   * the editor can tint what follows the data. */
  readonly runs = computed<ShownRun[]>(() => {
    if (!this.isText()) return [];
    const known = this.placeholders();
    return tokenize(this.element().content ?? '', known).map((run) =>
      run.kind === 'text'
        ? { text: run.value, placeholder: false, label: '' }
        : {
            text: placeholderSample(known.get(run.key)!, this.longTexts()),
            placeholder: true,
            label: known.get(run.key)!.label,
          },
    );
  });
  /** The values' names over their runs, measured after every render of the text. */
  readonly valueLabels = signal<ValueLabel[]>([]);
  readonly sampleText = computed(() =>
    this.runs()
      .map((run) => run.text)
      .join(''),
  );
  /** A text that prints nothing for this company (every value it uses is missing). */
  readonly muted = computed(
    () => this.isText() && !wouldPrint(this.element().content ?? '', this.placeholders()),
  );
  readonly badge = computed(() => {
    const issues = this.issues();
    if (!issues || (issues.errors.length === 0 && issues.warnings.length === 0)) return null;
    const severity = issues.errors.length > 0 ? 'error' : 'warning';
    const title = this.label();
    const lines = [...issues.errors, ...issues.warnings].map((line) => stripLabel(line, title));
    return { severity, text: lines.join(' · '), count: lines.length };
  });
  /** Counter the canvas zoom so the badge stays about 18 screen pixels. */
  readonly badgeStyle = computed(() => `transform: scale(${1 / (this.scale() * PX_PER_PT)})`);
  /** A value label hangs from its run's top right corner, at screen size. */
  readonly labelTransform = computed(
    () => `translate(-100%, -100%) scale(${1 / (this.scale() * PX_PER_PT)})`,
  );
  readonly ariaLabel = computed(() => {
    const e = this.element();
    return `${this.label()}, Position ${Math.round(e.x)} / ${Math.round(e.y)} pt, Größe ${Math.round(e.width)} × ${Math.round(e.height)} pt`;
  });

  constructor() {
    // After every render that touched text, font or box: start from the set font size
    // and shrink exactly as the PDF does.
    afterRenderEffect(() => {
      this.style();
      const runs = this.runs();
      const box = this.box()?.nativeElement;
      const font = this.element().font;
      if (!box || !font) return;
      box.style.fontSize = `${font.size}pt`;
      const inner = box.firstElementChild;
      if (inner) {
        // text nodes and tinted spans, never HTML: the content is the merchant's
        inner.replaceChildren(
          ...runs.map((run) => {
            if (!run.placeholder) return document.createTextNode(run.text);
            const span = document.createElement('span');
            span.className = 'ph';
            // inline, not in the component's CSS: that is view-scoped and never
            // reaches a span made by hand
            span.style.cssText = PLACEHOLDER_TINT;
            span.textContent = run.text;
            span.dataset['label'] = run.label;
            return span;
          }),
        );
      }
      fitVoucherTextBox(box);
      // The box clips its content, so the names are drawn outside it, where each
      // run's top right corner ended up (in pt, like everything on the page).
      const k = this.scale() * PX_PER_PT;
      const origin = this.host.getBoundingClientRect();
      this.valueLabels.set(
        Array.from(box.querySelectorAll<HTMLElement>('.ph')).map((span) => {
          const rect = span.getClientRects()[0] ?? span.getBoundingClientRect();
          return {
            text: span.dataset['label'] ?? '',
            x: (rect.right - origin.left) / k,
            y: (rect.top - origin.top) / k,
          };
        }),
      );
    });
  }

  onClick(event: MouseEvent) {
    event.stopPropagation();
  }

  onSurfacePointerDown(event: PointerEvent) {
    if (event.button !== 0) return;
    event.stopPropagation();
    const pan = this.panTarget(event);
    if (pan) {
      this.select.emit({ id: this.element().id, toggle: false });
      this.pointerStart.emit({ id: this.element().id, mode: 'pan', event, natural: pan });
      return;
    }
    const toggle = event.shiftKey || event.metaKey || event.ctrlKey;
    const wasSelected = this.selected();
    this.select.emit({ id: this.element().id, toggle });
    if (!toggle)
      this.pointerStart.emit({ id: this.element().id, mode: 'move', event, wasSelected });
  }

  /** ⌘/Strg + drag on a picture that fills its box pans the crop instead of moving the box. */
  private panTarget(event: PointerEvent): { width: number; height: number } | null {
    if (!(event.metaKey || event.ctrlKey || event.altKey) || !this.canPan()) return null;
    const img = this.img()?.nativeElement;
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;
    return { width: img.naturalWidth, height: img.naturalHeight };
  }

  onHandlePointerDown(handle: Handle, event: PointerEvent) {
    if (event.button !== 0) return;
    event.stopPropagation();
    this.pointerStart.emit({ id: this.element().id, mode: 'resize', handle, event });
  }

  onKeydown(event: KeyboardEvent) {
    const move = this.arrowDelta(event);
    if (move) {
      event.preventDefault();
      if (!this.selected()) this.select.emit({ id: this.element().id, toggle: false });
      this.nudge.emit(move);
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this.remove.emit(this.element().id);
    }
  }

  onHandleKeydown(handle: Handle, event: KeyboardEvent) {
    const move = this.arrowDelta(event);
    if (!move) return;
    event.preventDefault();
    this.keyResize.emit({ id: this.element().id, handle, ...move });
  }

  private arrowDelta(event: KeyboardEvent): { dx: number; dy: number } | null {
    const step = event.shiftKey ? 10 : 1;
    switch (event.key) {
      case 'ArrowLeft':
        return { dx: -step, dy: 0 };
      case 'ArrowRight':
        return { dx: step, dy: 0 };
      case 'ArrowUp':
        return { dx: 0, dy: -step };
      case 'ArrowDown':
        return { dx: 0, dy: step };
      default:
        return null;
    }
  }
}
